import React, { useState } from "react";
import { Card, Row, Col } from "react-bootstrap";
import { TextField } from "@mui/material";
import toast from "react-hot-toast";
import { ethers } from "ethers";
import trinityManagerArtifact from "../../../../configs/abis/trinity-manager.json";
import config from "../../../../configs";

const { abi: trinityManagerAbi } = trinityManagerArtifact;

const MintPanel = ({ signer, onMinted, adminAddresses = [], currentUser }) => {
  const [recipient, setRecipient] = useState("");
  const [amount, setAmount] = useState("");
  const [referrer, setReferrer] = useState("");
  const [loading, setLoading] = useState(false);
  const managerAddress = config.get("smartContract.TRINITY_MANAGER_ADDRESS");

  const isAuthorized =
    signer &&
    currentUser &&
    adminAddresses.some(
      (adminAddress) =>
        adminAddress && adminAddress.toLowerCase() === currentUser.toLowerCase()
    );

  const handleMint = async (event) => {
    event.preventDefault();
    if (!isAuthorized) {
      toast.error("You are not allowed to mint");
      return;
    }
    if (!ethers.utils.isAddress(recipient)) {
      toast.error("Invalid recipient address");
      return;
    }
    if (!amount || Number(amount) <= 0) {
      toast.error("Amount must be greater than zero");
      return;
    }
    // Validate referrer address if provided
    if (referrer && !ethers.utils.isAddress(referrer)) {
      toast.error("Invalid referrer address");
      return;
    }
    try {
      setLoading(true);
      const contract = new ethers.Contract(
        managerAddress,
        trinityManagerAbi,
        signer
      );
      // Use referrer if provided, otherwise use zero address
      const referrerAddress =
        referrer && ethers.utils.isAddress(referrer)
          ? referrer
          : ethers.constants.AddressZero;

      const tx = await contract.adminMintShares(
        recipient,
        ethers.BigNumber.from(String(amount)),
        referrerAddress
      );
      toast.loading("Submitting mint transaction...", { id: "mint" });
      const receipt = await tx.wait();
      const purchaseId = receipt.events?.find((e) => e.event === "Purchased")
        ?.args?.id;
      const successMessage = purchaseId
        ? `Mint transaction queued (Purchase ID: ${purchaseId}). Awaiting multisig approvals.`
        : "Mint transaction queued. Awaiting multisig approvals.";
      toast.success(successMessage, { id: "mint" });
      setRecipient("");
      setAmount("");
      setReferrer("");
      onMinted?.();
    } catch (error) {
      console.error("Mint failed", error);
      toast.error(error?.message || "Mint failed", { id: "mint" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      className="bg-live p-2 mb-4 h-100"
      style={{
        background:
          "linear-gradient(96.27deg, #eb6a1b12 12.79%, #ff00e20f 50.13%, #28245f0f 107.19%)",
      }}
    >
      <Card.Body>
        <div class="d-flex align-items-center justify-space-between w-100">
          <h6 className="mb-3">Mint Shares</h6>
        </div>
        <form onSubmit={handleMint} className="mint-form1">
          <Row>
            <Col xs={12} md={3}>
              <TextField
                label="Recipient address"
                variant="outlined"
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                fullWidth
                required
                InputProps={{ className: "mint-input" }}
                InputLabelProps={{ className: "mint-input" }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: "#ffffff",
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                    borderRadius: "999px",
                    "& fieldset": {
                      borderColor: "rgba(255, 255, 255, 0.3)",
                      borderRadius: "999px",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(255, 255, 255, 0.5)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#f8780c",
                    },
                    "&.Mui-focused": {
                      backgroundColor: "rgba(255, 255, 255, 0.08)",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "rgba(255, 255, 255, 0.7)",
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#f8780c",
                  },
                  "& .MuiOutlinedInput-input": {
                    color: "#ffffff",
                  },
                  "& .MuiOutlinedInput-input::placeholder": {
                    color: "rgba(255, 255, 255, 0.5)",
                    opacity: 1,
                  },
                }}
              />
            </Col>
            <Col xs={12} md={3}>
              <TextField
                label="Amount"
                variant="outlined"
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                fullWidth
                required
                InputProps={{ className: "mint-input" }}
                InputLabelProps={{ className: "mint-input" }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: "#ffffff",
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                    borderRadius: "999px",
                    "& fieldset": {
                      borderColor: "rgba(255, 255, 255, 0.3)",
                      borderRadius: "999px",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(255, 255, 255, 0.5)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#f8780c",
                    },
                    "&.Mui-focused": {
                      backgroundColor: "rgba(255, 255, 255, 0.08)",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "rgba(255, 255, 255, 0.7)",
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#f8780c",
                  },
                  "& .MuiOutlinedInput-input": {
                    color: "#ffffff",
                  },
                  "& .MuiOutlinedInput-input::placeholder": {
                    color: "rgba(255, 255, 255, 0.5)",
                    opacity: 1,
                  },
                }}
              />
            </Col>
            <Col xs={12} md={3}>
              <TextField
                label="Referrer address (optional)"
                variant="outlined"
                value={referrer}
                onChange={(e) => setReferrer(e.target.value)}
                fullWidth
                placeholder="0x..."
                InputProps={{ className: "mint-input" }}
                InputLabelProps={{ className: "mint-input" }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: "#ffffff",
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                    borderRadius: "999px",
                    "& fieldset": {
                      borderColor: "rgba(255, 255, 255, 0.3)",
                      borderRadius: "999px",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(255, 255, 255, 0.5)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#f8780c",
                    },
                    "&.Mui-focused": {
                      backgroundColor: "rgba(255, 255, 255, 0.08)",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "rgba(255, 255, 255, 0.7)",
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#f8780c",
                  },
                  "& .MuiOutlinedInput-input": {
                    color: "#ffffff",
                  },
                  "& .MuiOutlinedInput-input::placeholder": {
                    color: "rgba(255, 255, 255, 0.5)",
                    opacity: 1,
                  },
                }}
              />
            </Col>
            <Col xs={12} md={3}>
              <button
                type="submit"
                className={`presale-submit-btn flex-grow-1 ${
                  loading ? "disabled" : ""
                }`}
                disabled={loading || !isAuthorized}
                style={{ width: "100%" }}
              >
                {loading ? "Processing..." : "Mint Shares"}
              </button>
            </Col>
          </Row>
          {!isAuthorized && (
            <p className="text-center mt-2 text-warning">
              Connect with the authorized admin wallet to mint.
            </p>
          )}
        </form>
      </Card.Body>
    </Card>
  );
};

export default MintPanel;
